import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type BaseUser,
  DateUtils,
  PermissionArea,
  Utils,
} from '@pbt/pbt-ui-components'
import {
  Check as CheckIcon,
  Delete as DeleteIcon,
  Eye as EyeIcon,
} from '@pbt/pbt-ui-components/src/icons'

import type { PopperAction } from '~/components/common/ActionsPopper'
import PuiTabs from '~/components/common/PuiTabs'
import LandingWidget, {
  type LandingWidgetProps,
} from '~/components/dashboard/landing/widgets/LandingWidget'
import NoRecords from '~/components/dashboard/landing/widgets/NoRecords'
import TableWidget from '~/components/dashboard/landing/widgets/TableWidget'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { LandingType } from '~/constants/landingConstants'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import i18n from '~/locales/i18n'
import { deleteDiagnosisHistory } from '~/store/actions/medicalHistory'
import {
  fetchWidgetsData,
  getWidgetData,
  getWidgetDataIsLoading,
} from '~/store/duck/landing'
import {
  deleteDiagnosis,
  editDiagnosisStatusOutsideSoap,
  getDiagnosesIsLoading,
} from '~/store/duck/soapDiagnoses'
import { useDiagnosesPartitioning } from '~/store/hooks/diagnoses'
import { getCRUDByArea, getCurrentBusinessId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getMedicalHistoryIsSending } from '~/store/reducers/medicalHistory'
import {
  type DiagnosisSnapshotItem,
  DiagnosisSnapshotItemType,
  type WidgetColumn,
} from '~/types'
import { addOriginalBusinessId } from '~/utils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import DiagnosisNameCell from './DiagnosisNameCell'
import DiagnosisStatusCell from './DiagnosisStatusCell'
import ProblemsDateCell from './ProblemsDateCell'

const columns: WidgetColumn<DiagnosisSnapshotItem>[] = [
  {
    id: 'title',
    component: DiagnosisNameCell,
    width: 3.5,
    label: i18n.t('Common:NAME'),
  },
  {
    id: 'vetName',
    prop: ({ vet }) => Utils.getPersonString(vet),
    width: 2,
    label: i18n.t('Common:TEAM_MEMBER'),
  },
  {
    id: 'date',
    prop: ({ date }) => DateUtils.formatDate(date),
    width: 2.5,
    label: i18n.t('Common:DATE_TIME'),
  },
  {
    id: 'diagnosisStatus',
    component: DiagnosisStatusCell,
    width: 2.5,
    label: i18n.t('Common:STATUS'),
  },
]

const problemsColumns = (clientId: string, patientId: string) => [
  {
    id: 'title',
    component: DiagnosisNameCell,
    width: 2.5,
    label: i18n.t('Common:NAME'),
  },
  {
    id: 'vetName',
    prop: ({ vet }: { vet: BaseUser }) => Utils.getPersonString(vet),
    width: 2,
    label: i18n.t('Common:TEAM_MEMBER'),
  },
  {
    id: 'date',
    width: 2.5,
    component: ProblemsDateCell,
    label: i18n.t('Common:DATE_TIME'),
    componentProps: {
      clientId,
      patientId,
    },
  },
  {
    id: 'diagnosisStatus',
    component: DiagnosisStatusCell,
    width: 2,
    label: i18n.t('Common:STATUS'),
  },
]

const useStyles = makeStyles(
  (theme) => ({
    tabRoot: {
      padding: theme.spacing(1),
      fontSize: theme.typography.pxToRem(14),
    },
  }),
  { name: 'DiagnosesSnapshot' },
)

interface DiagnosesSnapshotProps
  extends Omit<
    LandingWidgetProps<DiagnosisSnapshotItem>,
    'component' | 'data'
  > {
  clientId: string
  name: string
  patientId: string
}

const ACTIVE_TAB_ID = 0
const HISTORIC_TAB_ID = 1

const createTab = (
  id: number,
  label: string,
  count: number,
  isLoading: boolean,
) => ({
  id,
  label: `${label}${isLoading ? '' : ` (${count})`}`,
})

const DiagnosesSnapshot = ({
  name,
  clientId,
  patientId,
  ...rest
}: DiagnosesSnapshotProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Clients'])
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const currentBusinessId = useSelector(getCurrentBusinessId)
  const diagnoses = useSelector(
    getWidgetData(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Problems,
    ),
  )
  const isLoading = useSelector(
    getWidgetDataIsLoading(
      LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
      SnapshotsAliasTypes.Problems,
    ),
  )
  const isDiagnosesLoading = useSelector(getDiagnosesIsLoading)
  const isDiagnosesHistoryLoading = useSelector(getMedicalHistoryIsSending)
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const {
    create: diagnoseCreatePermissions,
    delete: diagnoseDeletePermissions,
    read: diagnoseReadPermissions,
    update: diagnoseUpdatePermissions,
  } = useSelector(getCRUDByArea(PermissionArea.DIAGNOSE))
  const { read: soapReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.SOAP),
  )

  const [openDiagnosisHistoryDialog] = useDialog(DialogNames.DIAGNOSIS_HISTORY)
  const [selectedTab, setSelectedTab] = useState(0)

  const {
    ResolvedDiagnosisStatusId,
    AddedByAiDiagnosisStatusId,
    activeProblems,
    historicProblems,
    byLatestDate,
  } = useDiagnosesPartitioning(diagnoses)

  const filteredDiagnoses = (
    selectedTab === ACTIVE_TAB_ID ? activeProblems : historicProblems
  ).sort(byLatestDate)

  const refetchDiagnoses = () => {
    dispatch(
      fetchWidgetsData([SnapshotsAliasTypes.Problems], {
        quiet: false,
        landingType: LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
        patientId,
        pageSize: 150,
      }),
    )
  }

  const refetchDiagnosesAfterChange = useCloseAfterCreation(
    refetchDiagnoses,
    getDiagnosesIsLoading,
    true,
  )
  const refetchDiagnosesAfterHistoryChange = useCloseAfterCreation(
    refetchDiagnoses,
    getMedicalHistoryIsSending,
    true,
  )

  const goToSoap = (item: DiagnosisSnapshotItem) => {
    navigate(
      addOriginalBusinessId(
        `/soap/${item.soapId}`,
        isPatientSharingEnabled ? item.businessId : null,
      ),
    )
  }

  const handleView = (item?: DiagnosisSnapshotItem) => {
    openDiagnosisHistoryDialog({ clientId, patientId, diagnosis: item })
  }

  const handleGoToSoap = goToSoap

  const handleResolve = (item: DiagnosisSnapshotItem) => {
    dispatch(editDiagnosisStatusOutsideSoap(item, ResolvedDiagnosisStatusId))
    refetchDiagnosesAfterChange()
  }

  const handleAddDiagnose = () => {
    openDiagnosisHistoryDialog({ clientId, patientId })
    refetchDiagnosesAfterChange()
  }

  const handleDelete = (item?: DiagnosisSnapshotItem) => {
    if (!item) {
      return
    }
    if (item.soapId) {
      if (item.logId) {
        dispatch(deleteDiagnosis(item.soapId, item.logId))
      }
      refetchDiagnosesAfterChange()
    } else {
      dispatch(deleteDiagnosisHistory(item.id, patientId))
      refetchDiagnosesAfterHistoryChange()
    }
  }

  const getActions = (item?: DiagnosisSnapshotItem) => {
    const isCurrentContextItem =
      !isPatientSharingEnabled || item?.businessId === currentBusinessId

    return [
      !item?.soapId && {
        id: 'view',
        label: t('Common:VIEW_ACTION'),
        Icon: EyeIcon,
        onClick: diagnoseReadPermissions ? () => handleView(item) : undefined,
        disabled: !diagnoseReadPermissions,
      },
      item?.soapId && {
        id: 'goToSoap',
        label: t('Common:GO_TO_SOAP'),
        Icon: ArrowForwardIcon,
        onClick: soapReadPermissions ? () => handleGoToSoap(item) : undefined,
        disabled: !soapReadPermissions,
      },
      item?.soapId &&
        item?.statusId !== ResolvedDiagnosisStatusId &&
        item?.type !== DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG &&
        item?.type !== DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG && {
          id: 'resolve',
          label: t('Common:RESOLVE_ACTION'),
          Icon: CheckIcon,
          onClick: diagnoseUpdatePermissions
            ? () => handleResolve(item)
            : undefined,
          disabled: !diagnoseUpdatePermissions,
        },
      isCurrentContextItem &&
        item?.statusId !== AddedByAiDiagnosisStatusId &&
        item?.type !== DiagnosisSnapshotItemType.PROBLEM_DIAGNOSIS_LOG &&
        item?.type !== DiagnosisSnapshotItemType.PROBLEM_FINDING_LOG && {
          id: 'delete',
          label: t('Common:DELETE_ACTION'),
          Icon: DeleteIcon,
          onClick: diagnoseDeletePermissions
            ? () => handleDelete(item)
            : undefined,
          disabled: !diagnoseDeletePermissions,
        },
    ].filter(Boolean) as PopperAction[]
  }

  return (
    <LandingWidget
      showHeaderTitles
      showOverflow
      canNavigateToDetails={false}
      canNavigateToItemDetails={R.F}
      columns={problemsColumns(clientId, patientId)}
      component={TableWidget}
      data={filteredDiagnoses}
      getActions={getActions}
      isLoading={isLoading || isDiagnosesLoading || isDiagnosesHistoryLoading}
      name={name}
      noRecords={
        <NoRecords
          action={t('Common:ADD_DIAGNOSIS_HISTORY')}
          text={t('Clients:SNAPSHOTS_AND_RECORDS.NO_DIAGNOSES')}
          onClick={diagnoseCreatePermissions ? handleAddDiagnose : undefined}
        />
      }
      showPaddings={false}
      tabSection={
        <PuiTabs
          classes={{ tabRoot: classes.tabRoot }}
          selectedTab={selectedTab}
          tabs={[
            createTab(
              ACTIVE_TAB_ID,
              t('Clients:PROBLEMS_WIDGET.ACTIVE_CHRONIC'),
              activeProblems.filter(
                (problem) => problem.statusId !== AddedByAiDiagnosisStatusId,
              ).length,
              isLoading,
            ),
            createTab(
              HISTORIC_TAB_ID,
              t('Clients:PROBLEMS_WIDGET.HISTORIC'),
              historicProblems.filter(
                (problem) => problem.statusId !== AddedByAiDiagnosisStatusId,
              ).length,
              isLoading,
            ),
          ]}
          variant="standard"
          onSelectedTabChange={setSelectedTab}
        />
      }
      {...rest}
    />
  )
}

export default DiagnosesSnapshot
